import React from 'react';
import dayjs from 'dayjs';
import client from 'utils/client';
import DataGroup from 'components/DataGroup';
import { TransactionStatusPill, SettledPill, ChargePill } from 'components/StatusPill/StatusPill';
import { useToast } from 'components/Toast/useToast';
import { formatCurrency } from 'utils/helpers';

const Section = ({ title, children }) => (
  <div className='txn-detail-section'>
    <h3 className='txn-detail-section-title'>{title}</h3>
    <div className='modal-body-grid'>{children}</div>
  </div>
);

// Fields from provider_response already shown in the sections above. Everything
// else in the blob is rendered generically under "Additional provider details"
// so nothing from the provider is hidden.
const RENDERED_PROVIDER_KEYS = new Set([
  'Type', 'StatusDescription', 'CustomerName', 'MaskedPAN', 'Nuban',
  'Stan', 'TransactionReference', 'RetrievalReferenceNumber',
]);

const humanize = (key) => key
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const money = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? formatCurrency(n) : null;
};

// metadata.charge lives on Pay with Transfer transactions only; amounts are in
// naira, like every other money field in metadata.
const ChargeSection = ({ transaction, onUpdated }) => {
  const { onOpen } = useToast();
  const [retrying, setRetrying] = React.useState(false);
  const charge = transaction?.metadata?.charge;

  if (!charge) return null;

  function retryCharge() {
    setRetrying(true);
    client
      .post(`/transactions/${transaction.id}/retry-charge`)
      .then((res) => {
        onOpen({ type: 'success', message: 'Charge retry queued' });
        if (res.data?.data) onUpdated(res.data.data);
      })
      .catch((err) => {
        onOpen({ type: 'error', message: err?.message || 'Failed to retry charge' });
      })
      .finally(() => setRetrying(false));
  }

  return (
    <Section title='Pay with Transfer Charge'>
      <DataGroup title='Status' value={<ChargePill status={charge.status} />} />
      <DataGroup title='Amount' value={money(charge.amount)} />
      <DataGroup title='Attempts' value={charge.attempts ?? 0} />
      <DataGroup
        title='Last Attempt'
        value={charge.lastAttemptAt ? dayjs(charge.lastAttemptAt).format('MMMM DD, YYYY h:mma') : null}
      />
      <DataGroup
        title='Charged At'
        value={charge.chargedAt ? dayjs(charge.chargedAt).format('MMMM DD, YYYY h:mma') : null}
      />
      <DataGroup title='BankOne Reference' value={charge.bankOneReference} copyable />
      <DataGroup title='Last Error' value={charge.lastError} />
      {/* Only 'failed' is retryable — while 'pending', auto-retry is still running
          and a manual kick could race the worker into a double debit. */}
      {charge.status === 'failed' && (
        <div style={{ alignSelf: 'end' }}>
          <button type='button' className='btn-primary' onClick={retryCharge} disabled={retrying}>
            {retrying ? 'Retrying…' : 'Retry charge'}
          </button>
        </div>
      )}
    </Section>
  );
};

const TransactionDetails = ({ data: initialData }) => {
  // Local copy so a charge retry can refresh the modal in place.
  const [data, setData] = React.useState(initialData);
  const pr = data?.provider_response || {};
  const meta = data?.metadata || {};
  const amount = formatCurrency(data?.amount);

  const extraEntries = Object.entries(pr)
    .filter(([key, value]) => !RENDERED_PROVIDER_KEYS.has(key) && formatValue(value) !== null);

  return (
    <div>
      <div className='txn-detail-header'>
        <div>
          <div className='txn-detail-header-label'>Amount</div>
          <div className='txn-detail-header-amount'>{amount}</div>
        </div>
        <div className='txn-detail-header-right'>
          <TransactionStatusPill code={data?.status} />
          <div className='txn-detail-header-settled'>
            <span>Settled:</span>
            <SettledPill settled={!!data?.settled} />
          </div>
        </div>
      </div>

      <Section title='Transaction'>
        <DataGroup title='Reference' value={data?.reference} copyable />
        <DataGroup
          title='Date'
          value={dayjs(data?.transaction_date || data?.created_at).format('MMMM DD, YYYY h:mma')}
        />
        <DataGroup title='Provider' value={data?.provider} />
        <DataGroup title='Type' value={pr.Type} />
        <DataGroup title='Description' value={pr.StatusDescription} />
      </Section>

      <Section title='Customer'>
        <DataGroup title='Customer Name' value={pr.CustomerName} />
        <DataGroup title='Masked PAN' value={pr.MaskedPAN} copyable />
        <DataGroup title='Nuban' value={pr.Nuban} copyable />
      </Section>

      <Section title='Routing'>
        <DataGroup
          title='Merchant'
          value={data?.terminal?.merchant
            ? `${data.terminal.merchant.first_name || ''} ${data.terminal.merchant.last_name || ''}`.trim()
            : null}
        />
        <DataGroup title='Terminal' value={data?.terminal?.terminal_id} copyable />
      </Section>

      <Section title='Fees & Settlement'>
        <DataGroup title='GTI Fee' value={money(meta.gtiFee)} />
        <DataGroup title='GTI Fee Cap' value={money(meta.gtiFeeCap)} />
        <DataGroup title='Bank Fee' value={money(meta.bankFee)} />
        <DataGroup title='Bank Fee Cap' value={money(meta.bankFeeCap)} />
        <DataGroup title='Merchant Amount' value={money(meta.merchantAmount)} />
        <DataGroup title='Bank Amount' value={money(meta.bankAmount)} />
        <DataGroup title='Fee Spread' value={money(meta.feeSpread)} />
        <DataGroup title='Cap Spread' value={money(meta.capSpread)} />
        <DataGroup title='Fee Range' value={money(meta.feeRange)} />
        <DataGroup title='Cap Range' value={money(meta.capRange)} />
      </Section>

      <ChargeSection transaction={data} onUpdated={setData} />

      <Section title='Technical'>
        <DataGroup title='Stan' value={pr.Stan} copyable />
        <DataGroup title='Transaction Reference' value={pr.TransactionReference} copyable />
        <DataGroup title='Retrieval Reference Number' value={pr.RetrievalReferenceNumber} copyable />
      </Section>

      {extraEntries.length > 0 && (
        <Section title='Additional provider details'>
          {extraEntries.map(([key, value]) => (
            <DataGroup key={key} title={humanize(key)} value={formatValue(value)} />
          ))}
        </Section>
      )}
    </div>
  );
};

export default TransactionDetails;
